<script lang="ts">
  // libs
  import * as _ from "lodash";
  import { onMount } from "svelte";
  import * as LocaleString from "@shared/svelte/Components/DateFormatter/LocaleString.svelte";

  // components
  import Spinner from "@shared/svelte/Components/Spinner.svelte";
  import FormatMoney from "@shared/svelte/Components/FormatMoney.svelte";
  import PayInvoiceButton from "@shared/svelte/Components/PayInvoiceButton.svelte";
  import SourceDescription from "@retailer/svelte/Payments/PaymentDetails/SourceDescription.svelte";
  import Icon from "@shared/svelte/Components/Icon.svelte";

  // services
  import * as InvoiceService from "@retailer/services/invoice_service";
  import * as PaymentsService from "@retailer/services/payments_service";
  import Flash from "@shared/services/flashv2.js";

  // stores
  import { setFees, InvoiceStore } from "@retailer/stores/InvoiceStore";
  import { PaymentsStore } from "@retailer/stores/PaymentsStore";
  import { dwollaIdentityStore, userDisabled } from "@retailer/svelte/stores/DwollaIdentityStore";
  import { SuspensionStore } from "@retailer/stores/SuspensionStore";

  // types
  import type { Invoice, PaymentTransferTypeSmall } from "@shared/types/Invoice";

  // props
  export let invoice: Invoice;

  // data
  let shouldShowPayInvoicesModal = false;
  const currentDateString = new Date().toISOString();
  const pillClass = {
    overdue: "fizz-pill-red",
    failed: "fizz-pill-red",
    unpaid: "fizz-pill-gray",
    closed: "fizz-pill-gray",
    paid: "fizz-pill-green",
    pending: "fizz-pill-yellow",
    scheduled: "fizz-pill-yellow",
  };
  const successEmail = _.get(window, "$rootScope.C.SUCCESS_EMAIL") || "support@provi.com";

  // functions
  function buildScheduledDate(scheduledTransfer: PaymentTransferTypeSmall) {
    const dateString = scheduledTransfer ? scheduledTransfer.scheduled_for : currentDateString;
    return LocaleString.convertISODateStringToTimeZoneSafeFormattedLocaleDate(dateString);
  }

  function togglePayInvoicesModal() {
    shouldShowPayInvoicesModal = !shouldShowPayInvoicesModal;
  }

  const setScheduledTransferInfo = () => {
    scheduledTransfer = $InvoiceStore.invoice.payment_transfers.find(
      pt => pt.status == "scheduled",
    );
    paymentInstrumentNickname = scheduledTransfer?.payment_method?.nickname;
    scheduledDate = buildScheduledDate(scheduledTransfer);
  };

  async function cancelScheduledPayment() {
    await PaymentsService.cancel([invoice.id], cancelSuccessCallback, cancelErrorCallback);

    setScheduledTransferInfo();
  }

  const cancelSuccessCallback = async () => {
    await InvoiceService.get($InvoiceStore.invoice.id);
    PaymentsStore.set({ loaded: true, failed: false });
  };

  const cancelErrorCallback = res => {
    PaymentsStore.set({ loaded: true, failed: true, errors: res["errors"][0]["error"] });
    Flash.now({ type: "error", message: $PaymentsStore.errors });
  };

  function closedInvoice(invoice: Invoice): boolean {
    return invoice.invoice_status_type == "closed";
  }

  // lifecycle
  onMount(() => {
    (async () => {
      if (invoice.fee_paid) setFees(invoice.fee_paid.amount);
    })();
  });

  // reactives
  $: payment = invoice.debit_paid?.amount || 0;
  $: failedPayments = invoice.payment_transfers.filter(
    pt => pt.status == "failed" && pt.transfer_type != "credit_usage",
  );
  $: paidOrPending = () =>
    invoice.invoice_status_type == "paid" || invoice.invoice_status_type == "pending";
  $: scheduledTransfer = $InvoiceStore.invoice.payment_transfers.find(
    pt => pt.status == "scheduled",
  );
  $: showPaymentDetails = paidOrPending() || invoice.invoice_status_type === "scheduled";
  $: paymentInstrumentNickname = scheduledTransfer?.payment_method?.nickname;
  $: scheduledDate = buildScheduledDate(scheduledTransfer);
  $: userSuspendedForDistributor = $SuspensionStore.loaded
    ? $SuspensionStore.suspensions.some(
        suspension => suspension.distributor_id == invoice.distributor_id,
      )
    : false;
</script>

{#if !$PaymentsStore.loaded}
  <Spinner />
{/if}

<div data-grid-area="right">
  <h4 class="fizz-heading-4 fizz-inset-squish-24 fizz-border-bottom">
    Pay {invoice.distributor.name}
  </h4>
  <div class="fizz-inset-square-24 fizz-text-medium fizz-text-center">
    <p class="fizz-stack-8">{"Invoice #" + invoice.invoice_reference_id}</p>

    <p class="fizz-heading-2 fizz-stack-8">
      {#if payment == 0 || invoice.invoice_status_type == "closed"}
        <FormatMoney input={InvoiceService.getDisplayAmount(invoice)} formatType={"5"} />
      {:else}
        <FormatMoney input={payment} formatType={"5"} />
      {/if}
    </p>

    <div class="fizz-stack-16 fizz-flex-row-8 fizz-justify-center">
      <p>
        {#if paidOrPending()}Was due{:else}Due{/if} on {LocaleString.convertToFormattedLocaleDate(
          invoice.due_date,
        )}
      </p>
      {#if scheduledTransfer}<Icon
          type="ClockAfternoon"
          height={13}
          width={13}
          id="scheduled-clock"
        />{/if}
    </div>

    <p class="fizz-stack-24">
      <span style="text-transform:capitalize;" class={pillClass[invoice.invoice_status_type]}>
        {invoice.invoice_status_type}
      </span>
      {#if invoice.invoice_status_type == "overdue"}
        <span class="fizz-text-urgent">{invoice.days_overdue}</span>
      {/if}
    </p>

    {#if !paidOrPending() && !scheduledTransfer && !closedInvoice(invoice)}
      {#if !invoice.is_read_only}
        <PayInvoiceButton {invoice} />
      {/if}

      {#if userSuspendedForDistributor}
        <div class="fizz-alert-error fizz-text-left fizz-no-min-width">
          <p>
            Your account is temporarily suspended from making payments online with {invoice
              .distributor.name}. Contact your {invoice.distributor.name} sales rep for more information.
          </p>
        </div>
      {/if}

      {#if failedPayments.length}
        <div class="fizz-flow-16">
          {#each failedPayments as failedPayment}
            <div class="fizz-surface-red fizz-inset-square-16 fizz-flow-16 fizz-text-left">
              <p>
                <strong>Payment Submitted</strong><br />
                {LocaleString.convertToFormattedLocaleDate(failedPayment.created_at)}
              </p>

              <p>
                <strong>Payment Method</strong><br />
                {(failedPayment.payment_method || {}).nickname}
              </p>

              {#if invoice.submitted_by_user_email}
                <p>
                  <strong>Paid by</strong><br />
                  {invoice.submitted_by_user_email}
                </p>
              {/if}

              {#each failedPayment.messages as message}
                <p>{message.message}</p>
              {/each}
            </div>
          {/each}
        </div>
      {/if}
    {:else}
      {#if scheduledTransfer && $InvoiceStore.loaded}
        <div class="fizz-flex-row fizz-stack-16">
          <button
            class="fizz-button fizz-flex-item"
            on:click|preventDefault={cancelScheduledPayment}
            data-testid="cancel-pay-button"
            disabled={userSuspendedForDistributor}
          >
            Cancel
          </button>

          <button
            class="fizz-button fizz-flex-item"
            on:click|preventDefault={togglePayInvoicesModal}
            data-testid="modify-pay-button"
            disabled={!$dwollaIdentityStore.loaded || $userDisabled || userSuspendedForDistributor}
          >
            Modify
          </button>
        </div>
      {/if}

      {#if userSuspendedForDistributor}
        <div class="fizz-alert-error fizz-text-left fizz-no-min-width fizz-stack-16">
          <p>
            Your account is temporarily suspended from making payments online with {invoice
              .distributor.name}. Contact your {invoice.distributor.name} sales rep for more information.
          </p>
        </div>
      {/if}

      {#if showPaymentDetails}
        {#if invoice.payment_confirmation != null}
          <div class="fizz-stack-16 fizz-surface-gray fizz-inset-squish-16">
            <strong> Payment Confirmation Code </strong>
            <br />
            <strong class="fizz-heading-2">{invoice.payment_confirmation.confirmation_code}</strong>
          </div>
        {/if}
        <dl class="fizz-stack-16 fizz-surface-gray fizz-inset-squish-16">
          <ul class="fizz-flow-16 fizz-text-left">
            <li>
              <strong
                >{invoice.invoice_status_type === "paid"
                  ? "Payment Submitted"
                  : "Payment Scheduled"}</strong
              ><br />
              {LocaleString.convertToFormattedLocaleDate(invoice.payment_date_submitted)}
            </li>

            <li>
              <strong>Payment Method</strong><br />
              {invoice.payment_methods && invoice.payment_methods[0] && !closedInvoice(invoice)
                ? invoice.payment_methods[0].nickname
                : "--"}
            </li>

            <li>
              <strong>Paid by</strong><br />
              {#if invoice.retailer_user}
                {invoice.retailer_user.email}
              {:else}
                Autopay
              {/if}
            </li>
            <li>
              <span class="fizz-flex-row-4">
                <strong>Source</strong>
                <SourceDescription />
              </span>
              {#if paidOrPending()}
                ProviPay
              {:else if closedInvoice(invoice)}
                Distributor
              {/if}
            </li>
          </ul>
        </dl>
        <span class="fizz-text-small padding--small">
          If you believe this payment was made in error please <a href="mailto:{successEmail}"
            >contact us.</a
          >
        </span>
      {/if}
    {/if}
  </div>
</div>
